import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';

export type UrlFilterValue = string | string[];

export type UrlFilterDefaults = Record<string, UrlFilterValue>;

export interface UrlFilterState<T extends UrlFilterDefaults> {
  values: T;
  setValues: (next: T | ((prev: T) => T)) => void;
  /** Per key: whether the query string actually carried the param. */
  isFromUrl: Record<keyof T, boolean>;
}

const parseValue = (raw: string | null, fallback: UrlFilterValue): UrlFilterValue => {
  if (raw === null) return fallback;
  if (!Array.isArray(fallback)) return raw;
  const parts = raw
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  return parts.length === 0 ? fallback : parts;
};

const isSameValue = (a: UrlFilterValue, b: UrlFilterValue): boolean => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => item === b[i]);
  }
  return a === b;
};

/**
 * Keeps a set of filter values in the query string so they survive navigating
 * away and back, a refresh, and browser back/forward — and so a filtered view
 * can be shared as a link.
 *
 * Arrays are stored comma-separated; a key is dropped from the URL entirely
 * while it still holds its default, which keeps clean pages clean.
 *
 * `defaults` must be referentially stable — declare it as a module-level const
 * in the caller, not inline in the component body.
 */
export function useUrlFilterState<T extends UrlFilterDefaults>(defaults: T): UrlFilterState<T> {
  const location = useLocation();
  const navigate = useNavigate();

  const { values, isFromUrl } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const parsed = {} as T;
    const present = {} as Record<keyof T, boolean>;

    for (const key of Object.keys(defaults) as (keyof T & string)[]) {
      const raw = searchParams.get(key);
      parsed[key] = parseValue(raw, defaults[key]) as T[keyof T & string];
      present[key] = raw !== null;
    }

    return { values: parsed, isFromUrl: present };
  }, [location.search, defaults]);

  // The URL is not updated synchronously, so two setValues calls in the same
  // tick would both resolve against the same render's values and the second
  // would drop the first. Chain them through the last written result instead,
  // keyed on the search string it was written from.
  const pendingRef = useRef<{ from: string; values: T } | null>(null);

  const setValues = useCallback(
    (next: T | ((prev: T) => T)) => {
      const base =
        pendingRef.current?.from === location.search ? pendingRef.current.values : values;
      const resolved = typeof next === 'function' ? next(base) : next;
      pendingRef.current = { from: location.search, values: resolved };
      // Start from the live params so anything this hook does not own survives.
      const searchParams = new URLSearchParams(location.search);

      for (const key of Object.keys(defaults) as (keyof T & string)[]) {
        const value = resolved[key];
        if (value === undefined || isSameValue(value, defaults[key])) {
          searchParams.delete(key);
          continue;
        }
        searchParams.set(key, Array.isArray(value) ? value.join(',') : value);
      }

      const search = searchParams.toString();
      // replace, not push: otherwise every dropdown click adds a history entry
      // and Back would step through each filter tweak instead of leaving.
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : '' },
        { replace: true }
      );
    },
    [defaults, location.pathname, location.search, navigate, values]
  );

  return { values, setValues, isFromUrl };
}
